import React, { useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  Grid,
  Button,
  IconButton,
  VStack,
  Stack,
} from "@chakra-ui/react";
import { X } from "lucide-react";

export interface RescheduleData {
  requestor: string;
  appointment_date: string;
  appointment_time: string;
  reason: string;
}

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rescheduleData: RescheduleData;
  setRescheduleData: React.Dispatch<React.SetStateAction<RescheduleData>>;
  rescheduleErrors: Partial<RescheduleData>;
  handleRescheduleAppointment: () => void;
  isProcessing: boolean;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  rescheduleData,
  setRescheduleData,
  rescheduleErrors,
  handleRescheduleAppointment,
  isProcessing,
}) => {
  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={{ base: "full", sm: "sm", md: "md", lg: "lg", xl: "xl" }}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent
        maxW={{ base: "100vw", sm: "95vw", md: "600px" }}
        minH={{ base: "100vh", sm: "auto" }}
        h={{ base: "auto", sm: "388px" }}
        borderRadius={{ base: "none", sm: "0" }}
        mx={{ base: 0, sm: 4 }}
        my={{ base: 0, sm: "auto" }}
      >
        <ModalHeader
          fontSize={{ base: "lg", sm: "xl", md: "20px" }}
          fontWeight="700"
          pb={{ base: 2, sm: 3, md: 3 }}
          pt={{ base: 4, sm: 4 }}
          px={{ base: 4, sm: 5, md: 6 }}
          position="relative"
        >
          Reschedule
          <IconButton
            aria-label="Close"
            icon={<X size={20} />}
            onClick={onClose}
            position="absolute"
            right={{ base: 3, sm: 4 }}
            top={{ base: 3, sm: 4 }}
            size={{ base: "sm", sm: "sm" }}
            variant="link"
            color="#0A1B39"
            _hover={{ color: "#DC3611" }}
            _focus={{ boxShadow: "none" }}
          />
        </ModalHeader>

        <ModalBody
          px={{ base: 4, sm: 5, md: 6 }}
          py={{ base: 3, sm: 4 }}
          flex="1"
          overflowY="auto"
        >
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={{ base: 4, md: 2 }}
            mb={{ base: 4, md: 4 }}
          >
            {/* Requestor */}
            <FormControl isInvalid={!!rescheduleErrors.requestor} flex="1">
              <FormLabel
                fontSize={{ base: "xs", sm: "xs" }}
                fontWeight="500"
                mb={{ base: 1, sm: 2 }}
              >
                Requestor
              </FormLabel>
              <Input
                placeholder="Enter"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                value={rescheduleData.requestor}
                onChange={(e) =>
                  setRescheduleData({
                    ...rescheduleData,
                    requestor: e.target.value,
                  })
                }
                size={{ base: "sm", sm: "md" }}
              />
              <FormErrorMessage fontSize="xs">
                {rescheduleErrors.requestor}
              </FormErrorMessage>
            </FormControl>

            {/* Date */}
            <FormControl
              isInvalid={!!rescheduleErrors.appointment_date}
              flex="1"
            >
              <FormLabel
                fontSize={{ base: "xs", sm: "xs" }}
                fontWeight="500"
                mb={{ base: 1, sm: 2 }}
              >
                Date
              </FormLabel>
              <Input
                type="date"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                value={rescheduleData.appointment_date}
                min={new Date().toISOString().split("T")[0]}
                ref={dateRef}
                onClick={() => dateRef.current?.showPicker()}
                onChange={(e) =>
                  setRescheduleData({
                    ...rescheduleData,
                    appointment_date: e.target.value,
                  })
                }
                size={{ base: "sm", sm: "md" }}
              />
              <FormErrorMessage fontSize="xs">
                {rescheduleErrors.appointment_date}
              </FormErrorMessage>
            </FormControl>

            {/* Time */}
            <FormControl
              isInvalid={!!rescheduleErrors.appointment_time}
              flex="1"
            >
              <FormLabel
                fontSize={{ base: "xs", sm: "xs" }}
                fontWeight="500"
                mb={{ base: 1, sm: 2 }}
              >
                Time
              </FormLabel>
              <Input
                type="time"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                value={rescheduleData.appointment_time}
                ref={timeRef}
                onClick={() => timeRef.current?.showPicker()}
                onChange={(e) =>
                  setRescheduleData({
                    ...rescheduleData,
                    appointment_time: e.target.value,
                  })
                }
                size={{ base: "sm", sm: "md" }}
              />
              <FormErrorMessage fontSize="xs">
                {rescheduleErrors.appointment_time}
              </FormErrorMessage>
            </FormControl>
          </Stack>

          {/* Reason */}
          <FormControl isInvalid={!!rescheduleErrors.reason}>
            <FormLabel
              fontSize={{ base: "xs", sm: "xs" }}
              fontWeight="500"
              mb={{ base: 1, sm: 2 }}
            >
              Reason
            </FormLabel>
            <Textarea
              minH={{ base: "80px", sm: "100px" }}
              placeholder="Type Here..."
              _hover={{ borderColor: "#DC3611" }}
              _focus={{
                borderColor: "#DC3611",
                boxShadow: "0 0 0 1px #DC3611",
              }}
              value={rescheduleData.reason}
              onChange={(e) =>
                setRescheduleData({ ...rescheduleData, reason: e.target.value })
              }
              size={{ base: "sm", sm: "md" }}
              fontSize={{ base: "sm", sm: "md" }}
            />
            <FormErrorMessage fontSize="xs">
              {rescheduleErrors.reason}
            </FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter
          px={{ base: 4, sm: 5, md: 6 }}
          py={{ base: 3, sm: 4 }}
          gap={{ base: 2, sm: 3 }}
          flexDirection={{ base: "column", sm: "row" }}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            isDisabled={isProcessing}
            width={{ base: "full", sm: "auto" }}
            size={{ base: "sm", sm: "md" }}
          >
            Close
          </Button>
          <Button
            bg="#DC3611"
            color="white"
            _hover={{ bg: "#bf3c1f" }}
            onClick={handleRescheduleAppointment}
            isLoading={isProcessing}
            loadingText="Rescheduling..."
            width={{ base: "full", sm: "auto" }}
            size={{ base: "sm", sm: "md" }}
          >
            Update Schedule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RescheduleModal;
